import React, { useState, useRef, useEffect } from 'react';
import { Box, Skeleton } from '@mui/material';
import StaticMapImage from './StaticMapImage';
import { StaticMapImageProps } from './StaticMapInterface';

/**
 * LazyStaticMap Component
 * 
 * Implements lazy loading for static maps using Intersection Observer.
 * Only loads the map when it's about to enter the viewport.
 */

interface LazyStaticMapProps extends StaticMapImageProps {
  /** Root margin for intersection observer (default: '50px') */
  rootMargin?: string;
  /** Intersection threshold (default: 0.1) */
  threshold?: number;
  /** Show skeleton placeholder while not visible (default: true) */
  showSkeleton?: boolean;
}

const LazyStaticMap: React.FC<LazyStaticMapProps> = ({
  rootMargin = '50px',
  threshold = 0.1,
  showSkeleton = true,
  width,
  height,
  onLoad,
  className,
  style,
  ...staticMapProps
}) => {
  const [isVisible, setIsVisible] = useState(false);
  const [isLoaded, setIsLoaded] = useState(false);
  const containerRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          setIsVisible(true);
          observer.disconnect();
        }
      },
      { 
        threshold,
        rootMargin
      }
    );

    if (containerRef.current) {
      observer.observe(containerRef.current);
    }

    return () => observer.disconnect();
  }, [threshold, rootMargin]);

  const handleImageLoad = () => {
    setIsLoaded(true);
    if (onLoad) {
      onLoad();
    }
  };

  return (
    <Box
      ref={containerRef}
      className={className}
      sx={{
        width,
        height,
        position: 'relative',
        overflow: 'hidden',
        ...style
      }}
    >
      {isVisible ? (
        <StaticMapImage
          {...staticMapProps}
          width={width}
          height={height}
          onLoad={handleImageLoad}
          style={{
            width: '100%',
            height: '100%',
            objectFit: 'cover',
            opacity: isLoaded ? 1 : 0,
            transition: 'opacity 0.3s ease-in-out'
          }}
        />
      ) : (
        showSkeleton && (
          <Skeleton
            variant="rectangular"
            width="100%"
            height="100%"
            animation="wave"
            sx={{
              bgcolor: 'grey.100',
              '&::after': {
                background: 'linear-gradient(90deg, transparent, rgba(255,255,255,0.4), transparent)'
              }
            }}
          />
        )
      )}
      
      {/* Loading overlay */}
      {isVisible && !isLoaded && (
        <Box
          sx={{
            position: 'absolute',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            backgroundColor: 'rgba(0, 0, 0, 0.1)',
            zIndex: 1
          }}
        >
          <Skeleton
            variant="rectangular"
            width="100%"
            height="100%"
            animation="pulse"
          />
        </Box>
      )}
    </Box>
  );
};

export default LazyStaticMap;